/**
 * ResultEntryQueryPanel — the structured query/filter region for Result Entry
 * Express, ported from the legacy BTLIMS Result Entry query panel.
 *
 * Layout (2026-08-27): a fixed 7-column × 2-row grid —
 *   col1 QC Batch ID / Job ID · col2 Sample Matrix / Test · col3 Project ID /
 *   Project Name · col4 Client Name / Sample Location · col5 Date Analyzed F/T ·
 *   col6 Date Received F/T · col7 Date Collected F/T (SOON). Each date column
 *   keeps a SINGLE label on its From cell; the To cell sits directly below.
 * Lookup filters are CHECKED COMBOS (multi-select) with a header select-all;
 * values are sent '|'-joined and matched server-side (IN via STRING_SPLIT).
 */
import { useMemo, useState } from 'react';
import { LookupCombo } from '../../components/LookupCombo';
import './resultEntryQueryPanel.css';

/** A QC Batch option — value + the two display-only columns (batch Test + the
 *  Employee who created it) shown in the 3-column picker. */
export interface ReeBatchOption { id: string; test: string; emp: string }

/** The query-panel filter values. Empty array/'' = "All" (no filter). */
export interface ReeFilters {
  batchId:        string[];   // QC Batch ID (= QCBatchID)
  id:             string[];   // Job ID
  test:           string[];
  matrix:         string[];   // Sample Matrix
  clientName:     string[];
  projectId:      string[];
  projectName:    string[];
  sampleLocation: string[];   // Site Name (SampleLogIn.StationLocationName)
  receivedFrom:   string;
  receivedTo:     string;
  analyzedFrom:   string;
  analyzedTo:     string;
  // Present-but-disabled ("SOON") until its column is wired:
  collectedFrom:  string;
  collectedTo:    string;
}

export const EMPTY_REE_FILTERS: ReeFilters = {
  batchId: [], id: [], test: [], matrix: [], clientName: [], projectId: [],
  projectName: [], sampleLocation: [],
  receivedFrom: '', receivedTo: '', analyzedFrom: '', analyzedTo: '',
  collectedFrom: '', collectedTo: '',
};

/** Distinct dropdown options for the panel. */
export interface ReeOptions {
  clients:         string[];
  projects:        string[];
  projectNames:    string[];
  tests:           string[];
  matrices:        string[];
  jobIds:          string[];
  sampleLocations: string[];
  batchIds:        ReeBatchOption[];   // QC Batch ID · Test · Employee
}

export const EMPTY_REE_OPTIONS: ReeOptions = {
  clients: [], projects: [], projectNames: [], tests: [], matrices: [],
  jobIds: [], sampleLocations: [], batchIds: [],
};

/** True when any filter the server honours has a value. */
export function hasActiveReeFilters(f: ReeFilters): boolean {
  return !!(f.batchId.length || f.id.length || f.test.length || f.matrix.length
    || f.clientName.length || f.projectId.length || f.projectName.length
    || f.sampleLocation.length || f.receivedFrom || f.receivedTo
    || f.analyzedFrom || f.analyzedTo || f.collectedFrom || f.collectedTo);
}

const toLookup = (col: string, values: string[]) => ({ cols: [col], rows: values.map(v => [v]) });

/** Popup width sized to the longest option text. */
const popW = (values: string[], header: string) => {
  const longest = values.reduce((m, v) => Math.max(m, v.length), header.length);
  return Math.min(380, Math.max(180, Math.round(longest * 7 + 78)));
};

interface Props {
  filters: ReeFilters;
  onChange: (patch: Partial<ReeFilters>) => void;
  options: ReeOptions;
  loadingOptions?: boolean;
  applying?: boolean;
  onApply: () => void;
  onClear: () => void;
  /** Controlled collapse state — when provided the parent owns open/close. */
  collapsed?: boolean;
  onCollapsedChange?: (c: boolean) => void;
  /** Pending-entry counts shown inside the panel's bottom bar. */
  pendingBatches?: number;
  pendingJobs?: number;
  pendingLoading?: boolean;
}

export function ResultEntryQueryPanel({
  filters, onChange, options,
  applying: _applying = false, onApply: _onApply, onClear: _onClear,
  collapsed: collapsedProp,
  pendingBatches, pendingJobs, pendingLoading = false,
}: Props) {
  const [collapsedInternal] = useState(false);
  const collapsed = collapsedProp !== undefined ? collapsedProp : collapsedInternal;

  const recvError = useMemo(() =>
    filters.receivedFrom && filters.receivedTo && filters.receivedFrom > filters.receivedTo
      ? 'Date Received “From” must be on or before “To”.' : null,
    [filters.receivedFrom, filters.receivedTo]);
  const anaError = useMemo(() =>
    filters.analyzedFrom && filters.analyzedTo && filters.analyzedFrom > filters.analyzedTo
      ? 'Date Analyzed “From” must be on or before “To”.' : null,
    [filters.analyzedFrom, filters.analyzedTo]);
  const colError = useMemo(() =>
    filters.collectedFrom && filters.collectedTo && filters.collectedFrom > filters.collectedTo
      ? 'Date Collected “From” must be on or before “To”.' : null,
    [filters.collectedFrom, filters.collectedTo]);
  const dateError = recvError ?? anaError ?? colError;

  /** One single-column checked-combo field. */
  const combo = (label: string, values: string[], opts: string[], onToggle: (n: string[]) => void) => (
    <Field label={label}>
      <LookupCombo className="ree-lk" multi multiActions showFilter placeholder="All"
        popupClassName="ree-pick" popupWidth={popW(opts, label)}
        lookup={toLookup(label, opts)} values={values} onToggle={onToggle} />
    </Field>
  );

  /** One date input cell (From on row 1, or To on row 2 with an empty label). */
  const dateCell = (label: string, aria: string, value: string,
                    onVal: (v: string) => void, opts?: { soon?: boolean; error?: boolean }) => (
    <Field label={label} soon={opts?.soon} className={opts?.error ? 'ree-field-err' : undefined}>
      <input type="date" className="ree-date" aria-label={aria} disabled={opts?.soon}
        title={opts?.soon ? 'Coming soon — not yet available in this build.' : undefined}
        value={value} onChange={(e) => onVal(e.target.value)} />
    </Field>
  );

  // When collapsed, the outer control bar's Query toggle is the only affordance —
  // the panel disappears entirely (no title band left behind).
  if (collapsed) return null;

  return (
    <section className="ree-qp" aria-label="Query Panel">
      {/* 7-column × 2-row grid — row-major fill: children 1-7 = row 1, 8-14 = row 2. */}
      <div className="ree-qp-grid">
        {/* ── Row 1 ── */}
        <Field label="QC Batch ID">
          <LookupCombo className="ree-lk" multi multiActions showFilter placeholder="All"
            popupClassName="ree-pick" popupWidth={430} colWidths={[150, 170, 90]}
            lookup={{ cols: ['QC Batch ID', 'Test', 'Employee'],
                      rows: options.batchIds.map(b => [b.id, b.test, b.emp]) }}
            values={filters.batchId} onToggle={(n) => onChange({ batchId: n })} />
        </Field>
        {combo('Sample Matrix', filters.matrix,     options.matrices,     (n) => onChange({ matrix: n }))}
        {combo('Project ID',    filters.projectId,  options.projects,     (n) => onChange({ projectId: n }))}
        {combo('Client Name',   filters.clientName, options.clients,      (n) => onChange({ clientName: n }))}
        {dateCell('Date Analyzed-From',  'Date Analyzed from',  filters.analyzedFrom,  (v) => onChange({ analyzedFrom: v }),  { error: !!anaError })}
        {dateCell('Date Received-From',  'Date Received from',  filters.receivedFrom,  (v) => onChange({ receivedFrom: v }),  { error: !!recvError })}
        {dateCell('Date Collected-From', 'Date Collected from', filters.collectedFrom, (v) => onChange({ collectedFrom: v }), { error: !!colError })}

        {/* ── Row 2 ── */}
        {combo('Job ID',          filters.id,             options.jobIds,          (n) => onChange({ id: n }))}
        {combo('Test',            filters.test,           options.tests,           (n) => onChange({ test: n }))}
        {combo('Project Name',    filters.projectName,    options.projectNames,    (n) => onChange({ projectName: n }))}
        {combo('Sample Location', filters.sampleLocation, options.sampleLocations, (n) => onChange({ sampleLocation: n }))}
        {dateCell('Date Analyzed-To',  'Date Analyzed to',  filters.analyzedTo,  (v) => onChange({ analyzedTo: v }),  { error: !!anaError })}
        {dateCell('Date Received-To',  'Date Received to',  filters.receivedTo,  (v) => onChange({ receivedTo: v }),  { error: !!recvError })}
        {dateCell('Date Collected-To', 'Date Collected to', filters.collectedTo, (v) => onChange({ collectedTo: v }), { error: !!colError })}
      </div>
      {dateError && <div className="ree-inline-err ree-inline-err-row" role="alert">{dateError}</div>}

      {/* Pending-entry summary — shown at the bottom of the filters panel */}
      {(pendingBatches !== undefined || pendingJobs !== undefined) && (
        <div className="ree-qp-actions">
          <div className="ree-qp-pending">
            <i className="ti ti-clock-hour-4" aria-hidden="true" />
            <span className="ree-qp-pending-label">Pending Result Entry</span>
            <span className="ree-qp-pending-stat">
              <span className="ree-qp-pending-name">QC Batch</span>
              <span className={`ree-qp-pending-count${pendingLoading ? ' ree-qp-pending-loading' : (pendingBatches ?? 0) === 0 ? ' ree-qp-pending-zero' : ''}`}>
                {pendingLoading ? '…' : (pendingBatches ?? 0)}
              </span>
            </span>
            <span className="ree-qp-pending-stat">
              <span className="ree-qp-pending-name">Job ID</span>
              <span className={`ree-qp-pending-count${pendingLoading ? ' ree-qp-pending-loading' : (pendingJobs ?? 0) === 0 ? ' ree-qp-pending-zero' : ''}`}>
                {pendingLoading ? '…' : (pendingJobs ?? 0)}
              </span>
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

/** One compact label/field cell. An empty `label` renders a spacer so the row-2
 *  date "To" inputs align with the labelled controls beside them. */
function Field({ label, soon, className, children }: {
  label: string; soon?: boolean; className?: string; children: React.ReactNode;
}) {
  return (
    <div className={`ree-field${className ? ` ${className}` : ''}`}>
      <label className="ree-lbl">
        <span title={label || undefined}>{label || ' '}</span>
        {soon && <span className="ree-soon" title="Coming soon — not yet available in this build.">soon</span>}
      </label>
      <div className="ree-ctrl">{children}</div>
    </div>
  );
}
